import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { tripService } from '../services/tripService';
import { useAuth } from './AuthContext';
import type { Trip, CreateTripInput, UpdateTripInput } from '../types/trip';

interface TripContextValue {
  trips: Trip[];
  isLoading: boolean;
  error: Error | null;
  createTrip: (input: CreateTripInput) => Promise<Trip>;
  updateTrip: (id: string, input: UpdateTripInput) => Promise<Trip>;
  deleteTrip: (id: string) => Promise<void>;
  getTripById: (id: string) => Promise<Trip | undefined>;
  refreshTrips: () => Promise<void>;
}

const TripContext = createContext<TripContextValue | undefined>(undefined);

export function TripProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [trips, setTrips] = useState<Trip[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const hasFetchedRef = useRef(false);

  // Load trips only when authenticated (防止重複請求)
  useEffect(() => {
    // Reset state when user logs out
    if (!isAuthenticated) {
      setTrips([]);
      setError(null);
      hasFetchedRef.current = false;
      setIsLoading(false);
      return;
    }

    // Skip if already fetched
    if (hasFetchedRef.current) {
      return;
    }

    const loadTrips = async () => {
      try {
        setIsLoading(true);
        hasFetchedRef.current = true;
        const data = await tripService.getAll();
        setTrips(data);
      } catch (err) {
        setError(err as Error);
        hasFetchedRef.current = false;
      } finally {
        setIsLoading(false);
      }
    };

    loadTrips();
  }, [isAuthenticated]);

  /**
   * Refresh trips from backend
   */
  const refreshTrips = async () => {
    try {
      setIsLoading(true);
      const data = await tripService.getAll();
      setTrips(data);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Create a new trip
   */
  const createTrip = async (input: CreateTripInput): Promise<Trip> => {
    try {
      const newTrip = await tripService.create(input);
      setTrips((prev) => [...prev, newTrip]);
      setError(null);
      return newTrip;
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  /**
   * Update an existing trip
   */
  const updateTrip = async (id: string, input: UpdateTripInput): Promise<Trip> => {
    try {
      const current = trips.find((t) => t.id === id);
      if (!current) {
        throw new Error('找不到旅程');
      }
      // PUT /trip/{id} 是全量替換，所以用目前已知的 trip 資料補齊呼叫端沒有
      // 提供的欄位，避免只改名稱卻把日期一併清空。注意：trips 列表若是從
      // getAll()（列表 API）填入的，current 不會帶 description/destination/
      // travelerCount（列表 API 不回傳），呼叫端若要編輯這些欄位務必自己
      // 明確帶入完整值（EditTripPage 是直接呼叫 GET /trip/{id} 取得完整
      // 資料後再送出，不會踩到這個 fallback）。
      const merged = {
        name: input.name ?? current.name,
        description: input.description ?? current.description,
        destination: input.destination ?? current.destination,
        travelerCount: input.travelerCount ?? current.travelerCount,
        startDate: input.startDate ?? current.startDate,
        endDate: input.endDate ?? current.endDate,
      };
      const updated = await tripService.update(id, merged);
      setTrips((prev) => prev.map((t) => (t.id === id ? updated : t)));
      setError(null);
      return updated;
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  /**
   * Delete a trip
   */
  const deleteTrip = async (id: string): Promise<void> => {
    try {
      await tripService.delete(id);
      setTrips((prev) => prev.filter((t) => t.id !== id));
      setError(null);
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  /**
   * Get a single trip by ID
   * First checks local state, then fetches from backend if not found
   */
  const getTripById = async (id: string): Promise<Trip | undefined> => {
    // Check local state first
    const localTrip = trips.find((t) => t.id === id);
    if (localTrip) {
      return localTrip;
    }

    // If not in local state, fetch from backend
    try {
      const trip = await tripService.getById(id);
      if (trip) {
        // Add to local state for future access
        setTrips((prev) => [...prev, trip]);
        return trip;
      }
    } catch (err) {
      console.error('Failed to fetch trip by ID:', err);
    }

    return undefined;
  };

  return (
    <TripContext.Provider
      value={{
        trips,
        isLoading,
        error,
        createTrip,
        updateTrip,
        deleteTrip,
        getTripById,
        refreshTrips,
      }}
    >
      {children}
    </TripContext.Provider>
  );
}

export function useTrips() {
  const context = useContext(TripContext);
  if (context === undefined) {
    throw new Error('useTrips must be used within TripProvider');
  }
  return context;
}
